import * as React from 'react';
import { useMemo } from 'react';
import { Rater } from './models';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FLEX_ROW } from '../../../layout/flexBox';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { css, SerializedStyles } from '@emotion/core';
import { COLORS } from '../../../globalStyle/colors';
import { lastCommaFirstLocal } from './module';
import { useAppState } from '../../../core/redux/hooks';
import DeleteRaterModal from './DeleteRaterModal';
import { useToggle } from '../../../core/hooks/useToggle';
import { isValidDate } from '../../../language/time';
import LocalDiv from '../../../localization/LocalDiv';
import { doNothing } from '../../../language/module';


const styles = {
  actionIcon: css`
    font-size: 18px;
    color: ${COLORS.ICON_GREY};
    cursor: pointer;
  `,
  disabledIcon: css`
    cursor: default;
  `,
  nameAndEmail: css`
    line-height: 20px;
  `,
};


type Props = {
  className?: string;
  rater: Rater;
  showNames: boolean;
  showRelationship: boolean;
  showLanguage: boolean;
  approval: boolean;
  disableEditBtn: boolean;
  disableDeleteBtn: boolean;
  inviteStatus: React.ReactNode;
  approvalElements: React.ReactNode;
  setRater: (rater: Rater) => void;
  onConfirmDelete: () => void;
  iconStyle?: SerializedStyles;
  approverEmailSent: boolean;
}


const RaterTableRow: React.FC<Props> = props => {
  const dir                                      = useAppState(state => state.locale.textDirection);
  const [confirmDeleting, toggleConfirmDeleting] = useToggle();

  const raterNameTxt = useMemo(() => lastCommaFirstLocal(dir, props.rater) || '---', [dir, props.rater]);

  const approver = useMemo(() =>
    props.approval && props.rater.approvalStatus === 1,
    [props.approval, props.rater.approvalStatus],
  );
  const canEdit = useMemo(() =>
    props.rater.undeliveredEmail
    || (approver && !props.approverEmailSent)
    || (!approver && !isValidDate(props.rater.invitedDate)),
    [props.rater.undeliveredEmail, approver, props.approverEmailSent, props.rater.invitedDate],
  );

  const project = useAppState(state => state.projectSummary);
  const pastDue = useMemo(() => project?.surveyPastDue, [project]);

  return (
    <tr className={props.className} key={props.rater.raterId}>
      <td>
        <LocalDiv css={styles.nameAndEmail}>
          {props.showNames && <div css={FLEX_ROW}>{raterNameTxt}</div>}
          <div>{props.rater.email}</div>
        </LocalDiv>
      </td>
      {
        props.showRelationship && (
      <td>
        {props.rater.levelName || ''}
      </td>
        )
      }
      {
        props.showLanguage && (
      <td>
        {props.rater.languageNameNative || ''}
      </td>
        )
      }
      <td>
        {props.inviteStatus}
      </td>
      {props.approval && props.approvalElements}
      <td>
        {
          canEdit && !pastDue && (
            <FontAwesomeIcon
              icon={faPen}
              css={styles.actionIcon}
              onClick={() => props.disableEditBtn ? doNothing() : props.setRater(props.rater)}
            />
          )
        }
      </td>
      <td>
        { !pastDue && (
            <FontAwesomeIcon
              icon={faTrash}
              css={styles.actionIcon}
              onClick={props.disableDeleteBtn ? doNothing : toggleConfirmDeleting}
            /> 
          )         
        }
      </td>
      {
        confirmDeleting && (
          <DeleteRaterModal
            raterEmail={props.rater.email}
            onClickYes={props.onConfirmDelete}
            onDismiss={toggleConfirmDeleting}
          />
        )
      }
    </tr>
  );
};

export default RaterTableRow;
